/**
 * 動画生成ジョブのリアルタイム進捗を受信するSSEサービス
 */

import tokenManager from '../../utils/tokenManager';

interface VideoJobSSEData {
  type: string;
  job_id: string;
  status: string;
  progress_step: string;
  progress_percentage: number;
  error_message?: string;
}

interface VideoJobSSEHandler {
  onProgress?: (data: VideoJobSSEData) => void;
  onComplete?: (data: VideoJobSSEData) => void;
  onError?: (data: any) => void;
  onConnected?: (data: VideoJobSSEData) => void;
}

class VideoJobStreamService {
  private connections: Map<string, EventSource> = new Map();

  /**
   * ジョブの進捗ストリーミングを開始
   */
  public startStream(jobId: string, handlers: VideoJobSSEHandler): void {
    // Already connected
    if (this.connections.has(jobId)) {
      return;
    }

    const token = tokenManager.getToken();
    if (!token) {
      handlers.onError?.({ message: '認証トークンが見つかりません' });
      return;
    }

    const url = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/videos/jobs/${jobId}/stream?authorization=${encodeURIComponent('Bearer ' + token)}`;
    const eventSource = new EventSource(url);

    eventSource.onmessage = (event) => {
      try {
        const data: VideoJobSSEData = JSON.parse(event.data);

        switch (data.type) {
          case 'connected':
            handlers.onConnected?.(data);
            break;
          case 'progress':
            handlers.onProgress?.(data);
            break;
          case 'final':
            handlers.onComplete?.(data);
            this.stopStream(jobId);
            break;
          case 'error':
            handlers.onError?.(data);
            break;
        }
      } catch (error) {
        console.error('Failed to parse video job SSE message:', error);
      }
    };

    eventSource.onerror = () => {
      // SSE failed, will fall back to polling
      this.stopStream(jobId);
    };

    this.connections.set(jobId, eventSource);
  }

  /**
   * ジョブのストリーミングを停止
   */
  public stopStream(jobId: string): void {
    const eventSource = this.connections.get(jobId);
    if (eventSource) {
      eventSource.close();
      this.connections.delete(jobId);
    }
  }

  /**
   * 全ストリーミングを停止
   */
  public stopAll(): void {
    this.connections.forEach((es) => es.close());
    this.connections.clear();
  }

  /**
   * 接続中かどうか
   */
  public isStreaming(jobId: string): boolean {
    return this.connections.has(jobId);
  }
}

const videoJobStreamService = new VideoJobStreamService();
export default videoJobStreamService;
export type { VideoJobSSEData, VideoJobSSEHandler };
